"use client";

import { Button } from "@cmp-src/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@cmp-src/primitives/Select";
import type {
  ReportCatalog,
  ReportPeriod,
  ReportQuery,
  ReportQueryResult,
  ReportRecordsRequest
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useState } from "react";
import { useContributors } from "../hooks/useContributors";
import type { ChartSettings } from "../lib/chartSettings";
import { compareShortLabel, describeQuery } from "../lib/describeQuery";
import { downloadCsv, resultToRows } from "../lib/exportResult";
import { factsAsOfLabel } from "../lib/factsAsOf";
import {
  formatDelta,
  formatParts,
  formatValue,
  partsUnit
} from "../lib/formatValue";
import type { PairedResult } from "../lib/pair";
import {
  chartFormOf,
  pickForm,
  type ReportForm,
  shapeForForm
} from "../lib/pickForm";
import { recordsForQuery } from "../lib/recordsForQuery";
import { relatedQueries } from "../lib/relatedQueries";
import { AnimatedValue } from "./AnimatedValue";
import { CampusMapView, useCampusDivisions } from "./CampusMapView";
import { FloorPicker } from "./FloorPicker";
import { LineageCard } from "./LineageCard";
import { OfficeMapCard } from "./OfficeMapCard";
import { ReportEmptyState } from "./ReportEmptyState";
import { ReportMapView } from "./ReportMapView";
import { ReportSkeleton } from "./ReportSkeleton";
import { ResultChart } from "./ResultChart";
import { ResultTable } from "./ResultTable";
import { SmallMultiples } from "./SmallMultiples";
import { SplitCompare } from "./SplitCompare";

interface ResultPanelProps {
  catalog: ReportCatalog;
  query: ReportQuery;
  result: ReportQueryResult | undefined;
  isLoading: boolean;
  isError: boolean;
  form: ReportForm;
  onFormChange: (form: ReportForm) => void;
  onDrill: (dimension: string, memberId: string) => void;
  /** Follow-up questions from the side panel replace the whole query. */
  onQueryChange: (query: ReportQuery) => void;
  /** A drag across a trend or a click on a calendar day narrows the period only. */
  onZoom: (period: ReportPeriod) => void;
  /** "See the rows": switch to the dataset behind the number. */
  onShowRows?: (request: ReportRecordsRequest) => void;
  /** Earlier questions on the drill path, oldest first; click to go back. */
  trail?: ReportQuery[];
  onTrailBack?: (index: number) => void;
  /** Copies a link to this view, drill path included. */
  onShare?: () => Promise<void>;
  tokens: React.ReactNode;
  /** What the chart remembers (a bar order, hidden series...), kept by the caller. */
  chartSettings?: ChartSettings;
  onChartSettingsChange?: (patch: Partial<ChartSettings>) => void;
  /** A second measure's answer, drawn beside the first's. */
  paired?: PairedResult;
  /** A second measure is on its way: the chart keeps the shape it will need. */
  isPairing?: boolean;
}

/** The forms that draw two measures; the rest read one. */
const PAIRED_FORMS = new Set<ReportForm>([
  "auto",
  "trend",
  "bars",
  "share",
  "gauge",
  "table"
]);

const FORMS: { key: ReportForm; label: string }[] = [
  { key: "auto", label: "Auto" },
  { key: "trend", label: "Trend" },
  { key: "calendar", label: "Calendar" },
  { key: "bars", label: "Bars" },
  { key: "share", label: "Share" },
  { key: "gauge", label: "Gauge" },
  { key: "multiples", label: "Multiples" },
  { key: "stack", label: "Stacked" },
  { key: "grid", label: "Grid" },
  { key: "table", label: "Table" },
  { key: "map", label: "Map" }
];

/** Measures with a per-resource meaning, drawable on a floor plan. */
export const MAP_MEASURES = new Set([
  "reservations",
  "occupancy",
  "checkin_rate",
  "no_shows",
  "room_hours",
  "requests",
  "desks"
]);

/** The inventory has no period to colour by: its plan shows who may book each desk. */
export const mapModeFor = (measure: string): "period" | "assignments" =>
  measure === "desks" ? "assignments" : "period";

/** The floor a query is pinned to, when it is exactly one. */
export const queryFloor = (query: ReportQuery): string | null => {
  const floors = (query.filters ?? []).find((f) => f.dimension === "floor");
  return floors && floors.in.length === 1 ? floors.in[0] : null;
};

/** The offices a query is filtered to; none means all of them. */
export const queryOffices = (query: ReportQuery): string[] =>
  (query.filters ?? []).find((f) => f.dimension === "office")?.in ?? [];

/** Pins the query to one floor (a map needs one), dropping floor from the breakdown. */
export const withFloor = (
  query: ReportQuery,
  floorId: string
): ReportQuery => ({
  ...query,
  breakdown: (query.breakdown ?? []).filter((d) => d !== "floor"),
  filters: [
    ...(query.filters ?? []).filter((f) => f.dimension !== "floor"),
    { dimension: "floor", in: [floorId] }
  ]
});

interface MapFormProps {
  catalog: ReportCatalog;
  query: ReportQuery;
  onQueryChange: (query: ReportQuery) => void;
  /** Re-asserts the map form after a query change resets it. */
  onFormChange: (form: ReportForm) => void;
  onDrill: (dimension: string, memberId: string) => void;
}

/**
 * The map form: a floor plan coloured by the measure, or — when a division
 * has a map of its own — a campus with each office's zone coloured. The
 * floor is picked from the division tree, narrowed to the offices the
 * question is about: one office offers its floors alone.
 */
const MapForm: React.FC<MapFormProps> = ({
  catalog,
  query,
  onQueryChange,
  onFormChange,
  onDrill
}) => {
  const floorId = queryFloor(query);
  const campuses = useCampusDivisions();
  const [campusId, setCampusId] = useState<string | null>(null);
  const campus = campuses.find((d) => d.id === campusId) ?? null;
  const measure = catalog.measures.find((m) => m.key === query.measure);
  const canCampus =
    campuses.length > 0 && measure?.dimensions.includes("office");
  const picker = (
    <FloorPicker
      catalog={catalog}
      floorId={floorId}
      officeIds={queryOffices(query)}
      onPick={(id) => {
        // Changing the query resets the form to auto; both updates land in
        // the same tick, so the map stays.
        onQueryChange(withFloor(query, id));
        onFormChange("map");
      }}
    />
  );
  const campusPicker = canCampus ? (
    <Select
      value={campusId ?? ""}
      onValueChange={(id) => {
        setCampusId(id);
        onFormChange("map");
      }}
    >
      <SelectTrigger className="h-8 w-[220px] text-[13px]">
        <SelectValue>{campus?.name ?? "Campus map…"}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-dropdown">
        {campuses.map((d) => (
          <SelectItem key={d.id} value={d.id}>
            {d.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : null;
  if (!MAP_MEASURES.has(query.measure) && !canCampus) {
    return (
      <p className="py-16 text-center text-[13px] text-neutral-400">
        This measure has no place on a floor plan.
      </p>
    );
  }
  if (campus) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          {campusPicker}
          <button
            type="button"
            onClick={() => setCampusId(null)}
            className="text-[12px] text-neutral-500 hover:text-neutral-900"
          >
            Floor plan instead
          </button>
        </div>
        <CampusMapView
          catalog={catalog}
          division={campus}
          query={query}
          onDrill={onDrill}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {MAP_MEASURES.has(query.measure) ? picker : null}
        {campusPicker ? (
          <>
            {MAP_MEASURES.has(query.measure) ? (
              <span className="text-[12px] text-neutral-400">or</span>
            ) : null}
            {campusPicker}
          </>
        ) : null}
      </div>
      {floorId && MAP_MEASURES.has(query.measure) ? (
        <ReportMapView
          catalog={catalog}
          measure={query.measure}
          floorId={floorId}
          period={query.period}
          initialMode={mapModeFor(query.measure)}
        />
      ) : MAP_MEASURES.has(query.measure) && queryOffices(query).length > 0 ? (
        // An office filter draws the office's floors one at a time; a
        // floor picked above pins one of them.
        <OfficeMapCard
          catalog={catalog}
          measure={query.measure}
          officeIds={queryOffices(query)}
          period={query.period}
          initialMode={mapModeFor(query.measure)}
        />
      ) : (
        <p className="py-16 text-center text-[13px] text-neutral-400">
          Pick a floor to colour its desks and rooms by {query.measure}.
        </p>
      )}
    </div>
  );
};

interface StatProps {
  label: string;
  value: string;
  hint?: string;
}

const Stat: React.FC<StatProps> = ({ label, value, hint }) => (
  <div className="flex flex-col gap-0.5 rounded-lg bg-neutral-50 px-3 py-2.5">
    <span className="text-[10.5px] text-neutral-400 uppercase tracking-wider">
      {label}
    </span>
    <span className="font-semibold text-[17px] text-neutral-800 tabular-nums">
      {value}
    </span>
    {hint ? (
      <span className="truncate text-[11px] text-neutral-500">{hint}</span>
    ) : null}
  </div>
);

interface SideCardProps {
  title: string;
  children: React.ReactNode;
}

const SideCard: React.FC<SideCardProps> = ({ title, children }) => (
  <div className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-3.5">
    <span className="font-semibold text-[13px] text-neutral-900">{title}</span>
    {children}
  </div>
);

interface SidePanelProps {
  catalog: ReportCatalog;
  query: ReportQuery;
  result: ReportQueryResult | undefined;
  onQueryChange: (query: ReportQuery) => void;
  onDrill: (dimension: string, memberId: string) => void;
  onShowRows?: (request: ReportRecordsRequest) => void;
  onShowMap?: () => void;
}

interface ContributorsCardProps {
  catalog: ReportCatalog;
  query: ReportQuery;
  result: ReportQueryResult | undefined;
  onDrill: (dimension: string, memberId: string) => void;
}

/**
 * "What's behind it": the members that account for most of the number, one
 * line per free dimension, each a drill.
 */
const ContributorsCard: React.FC<ContributorsCardProps> = ({
  catalog,
  query,
  result,
  onDrill
}) => {
  const { groups, isLoading } = useContributors(
    catalog,
    query,
    result?.totals.value
  );
  const measure = result?.meta.measure;
  if (groups.length === 0) {
    return isLoading ? (
      <SideCard title="What's behind it">
        <span className="animate-pulse text-[12px] text-neutral-400">
          Looking…
        </span>
      </SideCard>
    ) : null;
  }
  return (
    <SideCard title="What's behind it">
      {groups.map((group) => (
        <div key={group.dimension} className="flex flex-col gap-1">
          <span className="text-[10.5px] text-neutral-400 uppercase tracking-wider">
            {group.dimensionLabel}
          </span>
          {group.contributors.map((c) => (
            <button
              type="button"
              key={c.memberId}
              onClick={() => onDrill(c.dimension, c.memberId)}
              className="group flex items-center gap-2 rounded-md py-0.5 text-left hover:bg-neutral-50"
              title={`Drill into ${c.label}`}
            >
              <span className="relative h-1.5 w-12 shrink-0 overflow-hidden rounded bg-neutral-100">
                <span
                  className="absolute inset-y-0 left-0 rounded bg-[#2a78d6]"
                  // Width is data-driven.
                  style={{
                    width: `${Math.round((c.share ?? (measure && measure.kind === "percent" ? c.value : 0)) * 100)}%`
                  }}
                />
              </span>
              <span className="flex-1 truncate text-[12px] text-neutral-700 group-hover:text-neutral-900">
                {c.label}
              </span>
              <span className="text-[11.5px] text-neutral-500 tabular-nums">
                {c.share !== null
                  ? `${Math.round(c.share * 100)}%`
                  : measure
                    ? formatValue(measure, c.value)
                    : ""}
              </span>
            </button>
          ))}
        </div>
      ))}
    </SideCard>
  );
};

/**
 * What the design called "behind the number" and "keep pulling the thread":
 * where the number comes from, and the next questions worth asking.
 */
const SidePanel: React.FC<SidePanelProps> = ({
  catalog,
  query,
  result,
  onQueryChange,
  onDrill,
  onShowRows,
  onShowMap
}) => {
  const hasCampus = useCampusDivisions().length > 0;
  const measure = catalog.measures.find((m) => m.key === query.measure);
  const related = relatedQueries(catalog, query);
  const breakdown = query.breakdown ?? [];
  const rows = onShowRows ? recordsForQuery(catalog, query) : null;
  const facts = result ? factsAsOfLabel(result.meta.facts_as_of) : null;

  return (
    <div className="flex flex-col gap-3">
      <ContributorsCard
        catalog={catalog}
        query={query}
        result={result}
        onDrill={onDrill}
      />

      {rows && onShowRows ? (
        <button
          type="button"
          onClick={() => onShowRows(rows)}
          className="rounded-xl border border-neutral-200 border-dashed px-4 py-3 text-left text-[12.5px] text-primary hover:bg-neutral-50"
        >
          See the rows behind this number →
        </button>
      ) : null}
      {onShowMap && (MAP_MEASURES.has(query.measure) || hasCampus) ? (
        <button
          type="button"
          onClick={onShowMap}
          className="rounded-xl border border-neutral-200 border-dashed px-4 py-3 text-left text-[12.5px] text-primary hover:bg-neutral-50"
        >
          Show it on the floor plan →
        </button>
      ) : null}

      {measure ? (
        <SideCard title="Behind the number">
          <LineageCard measure={measure} result={result} />
          {facts ? (
            <span
              className={cn(
                "rounded-md px-2 py-1 text-[11px]",
                facts.includes("older")
                  ? "bg-[#fdf0e8] text-[#8a3b12]"
                  : "bg-neutral-50 text-neutral-500"
              )}
            >
              {facts}
            </span>
          ) : null}
        </SideCard>
      ) : null}

      {related.length > 0 ? (
        <SideCard title="Keep pulling the thread">
          {related.map((r) => (
            <button
              type="button"
              key={r.label}
              onClick={() => onQueryChange(r.query)}
              className="text-left text-[12.5px] text-primary leading-[18px] hover:underline"
            >
              {r.label}
            </button>
          ))}
        </SideCard>
      ) : null}

      {breakdown.length > 0 ? (
        <span className="px-1 text-[11px] text-neutral-400">
          Click a {breakdown.length === 2 ? "cell" : "bar"} or a table row to
          drill into it.
        </span>
      ) : null}
    </div>
  );
};

/**
 * The answer: tokens, the headline number with its change, a sentence, the
 * chart in the form the query shape implies (or the one picked), a stats
 * strip, and the side panel. Keeps the last answer visible while the next
 * one loads.
 */
export const ResultPanel: React.FC<ResultPanelProps> = ({
  catalog,
  query,
  result,
  isLoading,
  isError,
  form,
  onFormChange,
  onDrill,
  onQueryChange,
  onZoom,
  onShowRows,
  trail,
  onTrailBack,
  onShare,
  tokens,
  chartSettings,
  onChartSettingsChange,
  paired,
  isPairing
}) => {
  // A pair on its way already shapes the chart, so nothing jumps when it
  // lands; one that failed to load is left off, and the chart reads alone.
  const isPaired = !!paired || !!isPairing;
  const resolvedForm = pickForm(query, form, isPaired);
  const pairedWith = paired?.measure.key;
  const [isCopied, setIsCopied] = useState(false);
  const share = async () => {
    if (!onShare) {
      return;
    }
    await onShare();
    setIsCopied(true);
    window.setTimeout(() => setIsCopied(false), 2000);
  };
  const isEmpty =
    !!result && result.rows.length === 0 && result.series.length === 0;
  const hasCampus = useCampusDivisions().length > 0;
  const measureDef = catalog.measures.find((m) => m.key === query.measure);
  // A share is a part of a whole: a count's members, a ratio's two parts,
  // or a pair of counts (two rates make no whole). A gauge reads a
  // percentage, or one count of a pair as a share of the other. A map
  // needs a measure with a place on a floor plan, or a campus to colour.
  const offeredForms = FORMS.filter((f) => {
    if (isPaired && !PAIRED_FORMS.has(f.key)) {
      return false;
    }
    if (f.key === "share") {
      return isPaired
        ? measureDef?.kind === "count"
        : measureDef?.kind === "count" || !!measureDef?.parts;
    }
    if (f.key === "gauge") {
      return isPaired
        ? measureDef?.kind === "count"
        : measureDef?.kind === "percent";
    }
    if (f.key === "map") {
      return MAP_MEASURES.has(query.measure) || hasCampus;
    }
    return true;
  });
  // Bars and grid need breakdowns; add them rather than ignore the click.
  // The query change resets the form to auto, so it is re-asserted after.
  // A pair's share is its two measures, so it needs no breakdown.
  const changeForm = (next: ReportForm) => {
    const shaped =
      isPaired && next === "share" ? query : shapeForForm(catalog, query, next);
    if (shaped !== query) {
      onQueryChange(shaped);
    }
    onFormChange(next);
  };
  const [splitMeasure, setSplitMeasure] = useState<string | null>(null);
  // The split is for one question; a new measure starts over with one chart.
  const isSplit = splitMeasure === query.measure;
  const measure = result?.meta.measure;
  // A pair is its own comparison: the badge beside the number gives way to
  // the second measure, as the charts and the table do.
  const delta =
    result && measure && !paired
      ? formatDelta(measure, result.totals.value, result.totals.previous)
      : null;
  // "312 booked of 1,040 desk-days": the numbers a percentage stands on.
  const parts =
    result && measure
      ? formatParts(measure, result.totals.parts, partsUnit(measure, query))
      : null;
  const ranked = result
    ? [...result.rows].sort((a, b) => b.value - a.value)
    : [];
  const highest = ranked[0];
  const lowest = ranked[ranked.length - 1];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_240px] gap-4">
      <div className="flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-5">
        {trail && trail.length > 0 && onTrailBack ? (
          <div className="flex flex-wrap items-center gap-1 text-[12px]">
            {trail.map((step, index) => (
              <React.Fragment key={`${index}-${describeQuery(catalog, step)}`}>
                <button
                  type="button"
                  onClick={() => onTrailBack(index)}
                  className="max-w-[220px] truncate rounded-full bg-neutral-100 px-2.5 py-0.5 text-neutral-600 hover:bg-neutral-200"
                  title={describeQuery(catalog, step)}
                >
                  {index === 0 ? "Start" : describeQuery(catalog, step)}
                </button>
                <span className="text-neutral-300">›</span>
              </React.Fragment>
            ))}
            <span className="max-w-[260px] truncate font-medium text-neutral-800">
              {describeQuery(catalog, query)}
            </span>
          </div>
        ) : null}
        {/* The question has the whole width; how to draw it sits on its own row. */}
        {tokens}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-0.5 rounded-lg border border-neutral-200 p-0.5">
            {offeredForms.map((f) => (
              <button
                type="button"
                key={f.key}
                onClick={() => changeForm(f.key)}
                className={cn(
                  "h-[26px] rounded-md px-2.5 text-[12px]",
                  form === f.key
                    ? "bg-neutral-900 font-medium text-white"
                    : "text-neutral-500 hover:bg-neutral-50"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSplitMeasure(isSplit ? null : query.measure)}
              title="Two members of a dimension, side by side"
            >
              {isSplit ? "One chart" : "Side by side"}
            </Button>
            {onShare ? (
              <Button variant="outline" size="sm" onClick={share}>
                {isCopied ? "Link copied" : "Share"}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              disabled={!result}
              onClick={() =>
                result &&
                downloadCsv(
                  describeQuery(catalog, query, pairedWith),
                  resultToRows(catalog, result, paired)
                )
              }
            >
              CSV
            </Button>
          </div>
        </div>

        <div className="flex items-end justify-between gap-5">
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-neutral-500">
              {describeQuery(catalog, query, pairedWith)}
            </span>
            <div className="flex items-center gap-3">
              {result && measure ? (
                <AnimatedValue
                  measure={measure}
                  value={result.totals.value}
                  className={cn(
                    "font-semibold text-[36px] text-neutral-800 tabular-nums leading-[40px]",
                    isLoading && "opacity-50"
                  )}
                />
              ) : (
                <span className="font-semibold text-[36px] text-neutral-300 leading-[40px]">
                  —
                </span>
              )}
              {delta ? (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-1 font-semibold text-[13px] tabular-nums",
                    delta.isFlat
                      ? "bg-neutral-100 text-neutral-500"
                      : delta.isGood
                        ? "bg-[#e8f6e8] text-[#006300]"
                        : "bg-[#fbe9e9] text-[#a52a2a]"
                  )}
                >
                  {delta.text}
                </span>
              ) : null}
            </div>
            {parts ? (
              <span className="text-[12px] text-neutral-500 tabular-nums">
                {parts}
              </span>
            ) : null}
          </div>
          {result ? (
            <span
              className="whitespace-nowrap text-[11.5px] text-neutral-400"
              title={factsAsOfLabel(result.meta.facts_as_of) ?? undefined}
            >
              {`${result.meta.from} – ${result.meta.to} · by ${result.meta.grain}`}
              {result.meta.office_scoped ? " · your offices" : ""}
              {result.meta.facts_as_of ? " · nightly facts" : ""}
            </span>
          ) : null}
        </div>

        <div className="min-h-[320px]">
          {isError ? (
            <p className="py-16 text-center text-[13px] text-semantic-error-foreground">
              This question couldn't be answered. Try a different breakdown.
            </p>
          ) : isSplit ? (
            <SplitCompare
              catalog={catalog}
              query={query}
              onClose={() => setSplitMeasure(null)}
            />
          ) : resolvedForm === "map" ? (
            <MapForm
              catalog={catalog}
              query={query}
              onQueryChange={onQueryChange}
              onFormChange={onFormChange}
              onDrill={onDrill}
            />
          ) : !result ? (
            <ReportSkeleton
              kind={
                resolvedForm === "trend"
                  ? "trend"
                  : resolvedForm === "table"
                    ? "rows"
                    : resolvedForm === "gauge"
                      ? "number"
                      : "bars"
              }
              className="py-4"
            />
          ) : isEmpty ? (
            <ReportEmptyState
              title="Nothing in this period"
              hint={
                (query.filters ?? []).length > 0
                  ? "The filters narrow this to nothing, or there was no activity in the window."
                  : "There was no activity in this window."
              }
              actions={[
                ...(query.period.preset !== "last_90_days"
                  ? [
                      {
                        label: "Try the last 90 days",
                        onClick: () =>
                          onQueryChange({
                            ...query,
                            period: { preset: "last_90_days" }
                          })
                      }
                    ]
                  : []),
                ...((query.filters ?? []).length > 0
                  ? [
                      {
                        label: "Clear filters",
                        onClick: () => onQueryChange({ ...query, filters: [] })
                      }
                    ]
                  : [])
              ]}
            />
          ) : resolvedForm === "table" ? (
            <ResultTable
              catalog={catalog}
              result={result}
              query={query}
              paired={paired}
              onDrill={onDrill}
            />
          ) : resolvedForm === "multiples" ? (
            <SmallMultiples query={query} result={result} onDrill={onDrill} />
          ) : (
            <ResultChart
              form={chartFormOf(resolvedForm)}
              result={result}
              query={query}
              paired={paired}
              onDrill={onDrill}
              onZoom={onZoom}
              settings={chartSettings}
              onSettingsChange={onChartSettingsChange}
            />
          )}
        </div>

        {result && measure ? (
          <div className="grid grid-cols-4 gap-2.5 border-neutral-100 border-t pt-3">
            <Stat
              label={measure.kind === "duration" ? "Median" : "Total"}
              value={formatValue(measure, result.totals.value)}
              hint={`${result.meta.from} – ${result.meta.to}`}
            />
            <Stat
              label="Highest"
              value={highest ? formatValue(measure, highest.value) : "—"}
              hint={highest?.labels.join(" · ")}
            />
            <Stat
              label="Lowest"
              value={lowest ? formatValue(measure, lowest.value) : "—"}
              hint={lowest?.labels.join(" · ")}
            />
            {paired ? (
              <Stat
                label={paired.measure.label}
                value={formatValue(measure, paired.result.totals.value)}
                hint="same period"
              />
            ) : (
              <Stat
                label={compareShortLabel(query.compare)}
                value={delta ? delta.text : "—"}
                hint={
                  result.meta.previous_from
                    ? `${result.meta.previous_from} – ${result.meta.previous_to}`
                    : "add a comparison"
                }
              />
            )}
          </div>
        ) : null}
      </div>

      <SidePanel
        catalog={catalog}
        query={query}
        result={result}
        onQueryChange={onQueryChange}
        onDrill={onDrill}
        onShowRows={onShowRows}
        onShowMap={() => onFormChange("map")}
      />
    </div>
  );
};
